import { useState } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrayerRequestPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    request: "",
    is_private: false,
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.request) {
      toast.error("Please fill in your name and prayer request.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/prayer-request`, form);
      setSent(true);
      toast.success("Prayer request submitted. We are praying with you.");
      setForm({ name: "", email: "", request: "", is_private: false });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="prayer-request-page">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            Prayer
          </p>
          <h1
            data-testid="prayer-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl"
          >
            Submit a Prayer Request
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            We believe in the power of prayer. Share your request and our church
            community will lift you up in prayer.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {sent ? (
            <div
              data-testid="prayer-success"
              className="bg-[#8F9779]/10 rounded-xl border border-[#8F9779]/30 p-10 text-center"
            >
              <Heart className="w-10 h-10 text-[#C8A97E] mx-auto mb-4" />
              <h3 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-2">
                Prayer Request Received
              </h3>
              <p className="text-sm text-[#595959] max-w-md mx-auto">
                Thank you for trusting us with your prayer request. Our community
                will be lifting you up in prayer.
              </p>
              <button
                data-testid="submit-another-prayer-btn"
                className="mt-6 text-sm text-[#C8A97E] hover:underline font-medium"
                onClick={() => setSent(false)}
              >
                Submit another request
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full bg-[#C8A97E]/10 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#C8A97E]" />
                </div>
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A]">
                    How Can We Pray for You?
                  </h2>
                  <p className="text-sm text-[#595959]">
                    All requests are treated with care and confidentiality.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} data-testid="prayer-form" className="space-y-5">
                <div>
                  <Label htmlFor="prayer-name" className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">
                    Your Name *
                  </Label>
                  <Input
                    id="prayer-name"
                    data-testid="prayer-name-input"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A]"
                  />
                </div>
                <div>
                  <Label htmlFor="prayer-email" className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">
                    Email (optional)
                  </Label>
                  <Input
                    id="prayer-email"
                    type="email"
                    data-testid="prayer-email-input"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A]"
                  />
                </div>
                <div>
                  <Label htmlFor="prayer-request" className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">
                    Prayer Request *
                  </Label>
                  <Textarea
                    id="prayer-request"
                    data-testid="prayer-request-input"
                    placeholder="Share your prayer request here..."
                    rows={6}
                    value={form.request}
                    onChange={(e) => setForm({ ...form, request: e.target.value })}
                    className="border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A] resize-none"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="is-private"
                    data-testid="prayer-private-checkbox"
                    checked={form.is_private}
                    onCheckedChange={(checked) =>
                      setForm({ ...form, is_private: checked === true })
                    }
                  />
                  <Label htmlFor="is-private" className="text-sm text-[#595959] cursor-pointer">
                    Keep my request private (visible only to church leadership)
                  </Label>
                </div>
                <button
                  type="submit"
                  data-testid="prayer-submit-btn"
                  disabled={loading}
                  className="bg-[#1C2E4A] text-white hover:bg-[#152238] px-8 py-3 rounded-md transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "Submit Prayer Request"}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
